import { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'midnight' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('qorder-theme') as Theme | null;
    if (savedTheme && ['light', 'midnight', 'dark'].includes(savedTheme)) {
      return savedTheme;
    }

    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    // Remove all theme classes first
    document.documentElement.classList.remove('light', 'dark', 'midnight');

    // Apply new theme class
    document.documentElement.classList.add(theme);

    // For Tailwind dark mode, we need the 'dark' class for both midnight and dark themes
    if (theme === 'midnight' || theme === 'dark') {
      document.documentElement.classList.add('dark');
    }

    // Add data attribute for custom styling
    document.documentElement.setAttribute('data-theme', theme);

    // Save to localStorage
    localStorage.setItem('qorder-theme', theme);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const cycleTheme = () => {
    setThemeState(prev => {
      if (prev === 'light') return 'midnight';
      if (prev === 'midnight') return 'dark';
      return 'light';
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, cycleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
